import React from 'react';
import type { JSONSchema7 } from 'json-schema';
import Button from 'src/modules/shared/components/atoms/buttons/button';
import { Text } from 'src/modules/shared/components/atoms/text';
import { isPlainObject, normalizeSchemaType } from './workflow-schema-utils';

type PathToken =
  | { kind: 'prop'; key: string }
  | { kind: 'index'; index: number };

const tokenizePath = (path: string): PathToken[] => {
  if (!path) return [];
  const tokens: PathToken[] = [];
  const regex = /([^[.\]]+)|\[(\d+)\]/g;
  let match: RegExpExecArray | null;
  while ((match = regex.exec(path)) !== null) {
    if (match[1]) {
      tokens.push({ kind: 'prop', key: match[1] });
    } else if (match[2]) {
      tokens.push({ kind: 'index', index: Number.parseInt(match[2], 10) });
    }
  }
  return tokens;
};

const tokensToPath = (tokens: PathToken[]): string =>
  tokens
    .map((t, idx) => {
      if (t.kind === 'prop') return idx === 0 ? t.key : `.${t.key}`;
      return `[${Number.isFinite(t.index) ? t.index : 0}]`;
    })
    .join('');

const collectObjectPropertyKeys = (
  schema: unknown,
  out = new Set<string>()
): Set<string> => {
  if (!isPlainObject(schema)) return out;
  const properties = (schema as { properties?: unknown }).properties;
  if (isPlainObject(properties)) {
    Object.keys(properties as Record<string, unknown>).forEach((k) =>
      out.add(k)
    );
  }
  (['allOf', 'anyOf', 'oneOf'] as const).forEach((key) => {
    const parts = (schema as Record<string, unknown>)[key];
    if (Array.isArray(parts)) {
      parts.forEach((p) => collectObjectPropertyKeys(p, out));
    }
  });
  return out;
};

const collectPropertySchemas = (
  schema: unknown,
  prop: string,
  out: unknown[] = []
): unknown[] => {
  if (!isPlainObject(schema)) return out;
  const properties = (schema as { properties?: unknown }).properties;
  if (isPlainObject(properties) && prop in properties) {
    out.push((properties as Record<string, unknown>)[prop]);
  }
  (['allOf', 'anyOf', 'oneOf'] as const).forEach((key) => {
    const parts = (schema as Record<string, unknown>)[key];
    if (Array.isArray(parts)) {
      parts.forEach((p) => collectPropertySchemas(p, prop, out));
    }
  });
  return out;
};

const collectArrayItemSchemas = (
  schema: unknown,
  out: unknown[] = []
): unknown[] => {
  if (!isPlainObject(schema)) return out;
  const items = (schema as { items?: unknown }).items;
  if (items !== undefined) out.push(items);
  (['allOf', 'anyOf', 'oneOf'] as const).forEach((key) => {
    const parts = (schema as Record<string, unknown>)[key];
    if (Array.isArray(parts)) {
      parts.forEach((p) => collectArrayItemSchemas(p, out));
    }
  });
  return out;
};

const mergeSchemasAsUnion = (schemas: unknown[]): unknown | undefined => {
  const filtered = schemas.filter((s) => s !== undefined);
  if (filtered.length === 0) return undefined;
  if (filtered.length === 1) return filtered[0];
  return { anyOf: filtered };
};

const getPropertySchema = (
  schemaRoot: unknown,
  prop: string
): unknown | undefined => {
  if (!schemaRoot) return undefined;
  const schemas = collectPropertySchemas(schemaRoot, prop);
  return mergeSchemasAsUnion(schemas);
};

const resolveSchemaAtTokens = (
  baseSchema: unknown,
  tokens: PathToken[]
): unknown | undefined => {
  let current: unknown = baseSchema;
  for (const token of tokens) {
    if (token.kind === 'prop') {
      current = getPropertySchema(current, token.key);
    } else {
      const itemSchemas = collectArrayItemSchemas(current);
      current = mergeSchemasAsUnion(itemSchemas);
    }
    if (!current) break;
  }
  return current;
};

type SchemaPathPickerProps = {
  schemaRoot?: JSONSchema7;
  options: string[];
  valuePath: string;
  onChangePath: (nextPath: string) => void;
  helperPrefix?: string;
};

export const SchemaPathPicker: React.FC<SchemaPathPickerProps> = ({
  schemaRoot,
  options,
  valuePath,
  onChangePath,
}) => {
  const tokens = React.useMemo(() => tokenizePath(valuePath), [valuePath]);
  const topFromValue =
    tokens.length > 0 && tokens[0].kind === 'prop' ? tokens[0].key : '';
  const topLevel = options.includes(topFromValue)
    ? topFromValue
    : options[0] || '';

  const nestedTokens =
    tokens.length > 0 && tokens[0].kind === 'prop' ? tokens.slice(1) : tokens;

  const baseSchema = React.useMemo(() => {
    if (!topLevel) return undefined;
    return getPropertySchema(schemaRoot, topLevel);
  }, [schemaRoot, topLevel]);

  const schemaAtCurrent = React.useMemo(
    () => resolveSchemaAtTokens(baseSchema, nestedTokens),
    [baseSchema, nestedTokens]
  );
  const currentType = normalizeSchemaType(schemaAtCurrent);
  const isArrayLike = React.useMemo(() => {
    if (currentType === 'array') return true;
    return (
      isPlainObject(schemaAtCurrent) &&
      (schemaAtCurrent as { items?: unknown }).items !== undefined
    );
  }, [currentType, schemaAtCurrent]);
  const propertyOptions = React.useMemo(
    () => Array.from(collectObjectPropertyKeys(schemaAtCurrent)).sort(),
    [schemaAtCurrent]
  );

  const pathTokens: PathToken[] = React.useMemo(() => {
    if (!topLevel) return [];
    return [{ kind: 'prop', key: topLevel }, ...nestedTokens];
  }, [nestedTokens, topLevel]);

  const canRemoveSegment = pathTokens.length > 1;

  const [arrayIndexDraft, setArrayIndexDraft] = React.useState('0');
  React.useEffect(() => {
    const last = nestedTokens[nestedTokens.length - 1];
    if (last && last.kind === 'index') {
      setArrayIndexDraft(String(last.index));
    } else {
      setArrayIndexDraft('0');
    }
  }, [nestedTokens]);

  if (!topLevel) {
    return (
      <select
        className="border rounded px-2 py-1.5 text-sm w-full"
        value=""
        onChange={(e) => {
          const next = e.target.value;
          if (!next) return;
          onChangePath(next);
        }}
      >
        <option value="" disabled>
          Selecciona…
        </option>
        {options.map((opt) => (
          <option key={`path-top-${opt}`} value={opt}>
            {opt}
          </option>
        ))}
      </select>
    );
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-col gap-1">
        <label className="text-xs text-slate-600">Campo</label>
        <select
          className="border rounded px-2 py-1.5 text-sm w-full"
          value={topLevel}
          onChange={(e) => {
            const next = e.target.value;
            if (!next) return;
            onChangePath(next);
          }}
        >
          {options.map((opt) => (
            <option key={`path-top-${opt}`} value={opt}>
              {opt}
            </option>
          ))}
        </select>
      </div>

      <div className="flex items-center justify-between gap-2">
        {canRemoveSegment && (
          <Button
            type="button"
            variant="neutral"
            appearance="ghost"
            size="xs"
            onClick={() => {
              const next = pathTokens.slice(0, -1);
              onChangePath(tokensToPath(next));
            }}
          >
            Quitar último
          </Button>
        )}
      </div>

      {isArrayLike && (
        <div className="flex flex-col gap-2 border border-slate-200 rounded p-2 bg-slate-50">
          <label className="text-xs text-slate-600">
            Índice del arreglo (elige un elemento)
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              className="border rounded px-2 py-1.5 text-sm w-full"
              value={arrayIndexDraft}
              onChange={(e) => setArrayIndexDraft(e.target.value)}
              min={0}
            />
            <Button
              type="button"
              variant="neutral"
              size="sm"
              onClick={() => {
                const parsed = Number.parseInt(arrayIndexDraft, 10);
                const nextIndex = Number.isNaN(parsed) ? 0 : parsed;
                const nextTokens = [
                  ...pathTokens,
                  { kind: 'index', index: nextIndex } as PathToken,
                ];
                onChangePath(tokensToPath(nextTokens));
              }}
            >
              Usar índice
            </Button>
          </div>
          <Text size="tiny" className="text-slate-500">
            Añade un índice para navegar dentro de la lista.
          </Text>
        </div>
      )}

      {propertyOptions.length > 0 && (
        <div className="flex flex-col gap-1">
          <label className="text-xs text-slate-600">Agregar subcampo</label>
          <select
            className="border rounded px-2 py-1.5 text-sm w-full"
            value=""
            onChange={(e) => {
              const nextKey = e.target.value;
              if (!nextKey) return;
              const nextTokens = [
                ...pathTokens,
                { kind: 'prop', key: nextKey } as PathToken,
              ];
              onChangePath(tokensToPath(nextTokens));
            }}
          >
            <option value="">Selecciona…</option>
            {propertyOptions.map((k) => (
              <option key={`path-prop-${k}`} value={k}>
                {k}
              </option>
            ))}
          </select>
        </div>
      )}
    </div>
  );
};

export default SchemaPathPicker;
